import {
  Text,
  IconCircleInfo,
  GreyGrey40,
  GreyGrey50,
  GreyGrey60,
  GreyGrey90,
  Primary13SemiBold,
  Primary13Regular,
  Primary20SemiBold,
  InfoPopover,
  XYChart,
  XYChartTooltipTemplate,
  Box,
  Flex
} from '@sequencehq/core-components'
import { useInvoicedRevenueWidget } from 'modules/Insights/widgets/InvoicedRevenueWidget/useInvoicedRevenueWidget'
import { FilterSelectInput } from 'modules/Insights/common/FilterSelectInput/FilterSelectInput'
import { Currency } from '@sequencehq/api'
import { toCurrencySymbol, toMoney } from '@sequencehq/utils'
import { WidgetLoading } from 'modules/Insights/common/WidgetLoading/WidgetLoading'
import { format, formatDateRange } from '@sequencehq/utils/dates'

interface EventTypesWidgetProps {
  dateRange: {
    start: Date
    end: Date
  }
}

export const InvoicedRevenueWidget = ({ dateRange }: EventTypesWidgetProps) => {
  const { isLoading, data, total, tickValues, tooltipFormat, currencyField } =
    useInvoicedRevenueWidget(dateRange)

  if (isLoading || !data) {
    return <WidgetLoading />
  }

  return (
    <Box
      width="390px"
      height="291px"
      boxShadow="0 2px 4px 0 rgba(15, 23, 42, 0.08)"
      border={`1px solid ${GreyGrey40}`}
      borderRadius="lg"
      p={4}
    >
      <Flex gap={2}>
        <Flex flexGrow="1" flexDirection="column" gap={2}>
          <Text as={Flex} {...Primary13SemiBold} color={GreyGrey60} gap={1}>
            Invoiced Revenue
            <InfoTooltip />
          </Text>
          <Text {...Primary20SemiBold} color={GreyGrey90}>
            {tooltipFormat(total)}
          </Text>
          <Text
            {...Primary13Regular}
            color={GreyGrey60}
            data-testid="insights.invoicedRevenueWidget.dateRange"
          >
            {formatDateRange({
              from: dateRange.start,
              to: dateRange.end
            })}
          </Text>
        </Flex>
        <Flex width="120px" alignSelf="baseline">
          <FilterSelectInput
            placeholder="Currency"
            value={currencyField.value}
            options={currencyField.options}
            onChange={currencyField.onChange}
          />
        </Flex>
      </Flex>
      <Box mt={4} height="170px" width="100%">
        <XYChart
          variant="bar"
          dateFrom={format(dateRange.start, 'yyyy-MM-dd')}
          dateTo={data[data.length - 1].date}
          data={data}
          unitTickValues={tickValues}
          unit={toCurrencySymbol(currencyField.value)}
          renderTooltip={({ date, value }) => (
            <DataTooltip
              date={date}
              value={value}
              currency={currencyField.value as Currency}
            />
          )}
        />
      </Box>
    </Box>
  )
}

const DataTooltip = ({
  date,
  value,
  currency
}: {
  date: string
  value: number
  currency: Currency
}) => {
  return (
    <XYChartTooltipTemplate>
      <Flex justifyContent="space-between">
        <Text {...Primary13SemiBold} color="gray.70">
          {format(new Date(date), 'MMMM yyyy')}
        </Text>
        <Text {...Primary13SemiBold} color="gray.70">
          {toMoney({
            value: value.toString(),
            currency
          })}
        </Text>
      </Flex>
      <>
        <Text {...Primary13SemiBold} color="gray.70">
          Total
        </Text>
        <Text {...Primary13SemiBold} color="gray.70">
          {toMoney({
            value: value.toString(),
            currency
          })}
        </Text>
      </>
    </XYChartTooltipTemplate>
  )
}

const InfoTooltip = () => {
  return (
    <InfoPopover
      variant="info"
      minimal
      padding="0"
      placement="top"
      body={
        <Box width={300}>
          Invoiced revenue is the total gross amount over all your sent invoices
          for the currency selected
        </Box>
      }
      icon={<IconCircleInfo color={GreyGrey50} />}
    />
  )
}
